// Intercalaires : la fin de l'empilement. Pendant que le contact arrive, la pile des sections ne
// recule pas sous lui : elle s'écarte et montre le bord de chaque carte sous la barre du haut,
// avec son nom, comme les onglets d'un classeur. Le contact se pose devant.
//
// Le geste passe par translate et scale, propriétés à part de transform : il s'ajoute au recul
// des cartes posé par GSAP (empilement.ts) sans jamais le toucher, et disparaît tout à fait avant
// son début. Sous mesure-flux, il est annulé comme le recul (mouvement.scss).

import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { RECUL } from './reglages.ts';

/** Hauteur d'un onglet, en pixels : le bord visible de chaque carte. */
const ONGLET = 22;

/** Écart d'échelle d'une carte à la suivante, plus profonde dans la pile. */
const PROFONDEUR = 0.012;

/**
 * Pose les intercalaires sous le contact ; rend la fonction qui les retire. bouger(carte) marque
 * une carte qui bouge : elle garde son dessin le temps du geste (empilement.ts).
 */
export function monterIntercalaires(
  main: HTMLElement,
  contact: HTMLElement,
  bouger: (carte: HTMLElement) => void,
): () => void {
  const cartes = [...main.querySelectorAll<HTMLElement>(':scope > [data-nav]')];
  const n = cartes.length;
  const etiquettes = cartes.map((c) => {
    const e = document.createElement('span');
    e.className = 'onglet';
    e.setAttribute('aria-hidden', 'true');
    e.textContent = c.dataset.nav ?? '';
    c.append(e);
    return e;
  });
  const repos = () => {
    for (const c of cartes) {
      c.style.removeProperty('translate');
      c.style.removeProperty('scale');
    }
    for (const e of etiquettes) {
      e.style.removeProperty('visibility');
      e.style.removeProperty('opacity');
    }
  };

  // Le geste de chaque carte, une fois le contact posé : son déplacement et son échelle. Toutes
  // les mesures d'abord, les écritures ensuite (placer) : une lecture après une écriture forcerait
  // le navigateur à recalculer les styles, à chaque carte et à chaque image.
  //
  // La place d'une carte se déduit du bas de <main>, mesuré (il colle, sans transformation) : une
  // carte est à sa place dans le flux (les cartes du dessous, espaces de fin compris, et sa marge
  // du haut la séparent du bas), ou plus bas si elle colle (son haut à min(0, H - h)), sans jamais
  // dépasser le bas de <main>. Le recul de l'empilement (toutes sauf la dernière) se fait autour
  // de oy. Les onglets se posent à des hauteurs fixes de l'écran, sous la barre.
  const cibles = () => {
    const H = innerHeight;
    const bas = main.getBoundingClientRect().bottom;
    const barre = document.querySelector<HTMLElement>('.top')?.offsetHeight ?? 0;
    const resultat: { y: number; echelle: number }[] = [];
    let dessous = 0;
    for (let k = n - 1; k >= 0; k--) {
      const c = cartes[k];
      if (!c) continue;
      const h = c.offsetHeight;
      dessous += h;
      const dessus = Math.min(Math.max(bas - dessous, Math.min(0, H - h)), bas - h);
      dessous += parseFloat(getComputedStyle(c).marginTop) || 0;
      const derniere = k === n - 1;
      const recul = derniere ? 1 : RECUL;
      const oy = derniere ? h / 2 : Math.max(h / 2, h - H / 2);
      const echelle = 1 - (n - 1 - k) * PROFONDEUR;
      resultat[k] = {
        y: barre + k * ONGLET - dessus - oy * (1 - echelle),
        echelle: echelle / recul,
      };
    }
    return resultat;
  };

  // p, de 0 à 1 : l'arrivée du contact.
  const placer = (p: number) => {
    if (p === 0) {
      repos();
      return;
    }
    const geste = cibles();
    cartes.forEach((c, k) => {
      const g = geste[k];
      if (!g) return;
      bouger(c);
      c.style.translate = `0 ${String(p * g.y)}px`;
      c.style.scale = String(1 + p * (g.echelle - 1));
    });
    // Les noms paraissent sur la seconde moitié du geste.
    const o = Math.max(0, (p - 0.5) / 0.5);
    for (const e of etiquettes) {
      e.style.visibility = o > 0 ? 'visible' : '';
      e.style.opacity = o > 0 ? String(o) : '';
    }
  };

  // Autour des cartes, la scène prend la couleur du contact dès qu'il paraît, et la rend quand il
  // repart.
  let scene: string | null = null;
  const st = ScrollTrigger.create({
    trigger: contact,
    start: 'top bottom',
    // Jusqu'au bout de la page : la place de la barre de navigation, sous le contact. <main> y
    // reste collé (la place est du contenu, pas une marge : ligne-de-temps.scss).
    end: 'max',
    onUpdate: (s) => {
      const p = Math.min(1, Math.max(0, (s.scroll() - s.start) / contact.offsetHeight));
      if (p > 0 && scene === null) {
        scene = document.body.style.background;
        document.body.style.background = getComputedStyle(contact).backgroundColor;
      } else if (p === 0 && scene !== null) {
        document.body.style.background = scene;
        scene = null;
      }
      placer(p);
    },
  });

  return () => {
    st.kill();
    repos();
    for (const e of etiquettes) e.remove();
  };
}
